import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  getErrorMessages,
  investmentsApi,
  useDeleteTransactionMutation,
  useGetInvestmentTransfersQuery,
  useLazyGetInvestmentTransfersQuery,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { InvestmentTransferItem } from "../../../../services/investments/types";
import {
  UseInvestmentContributionsProps,
  UseInvestmentContributionsReturn,
} from "./types";
import { PAGE_SIZE } from "../../../../utils/constants";

// Paginated via a base-query + lazy-append accumulator, reset by an effect on `fulfilledTimeStamp`.
// Reads `currentData`, never `data`: this component is reused across investment ids, and `data` would keep serving the previous investment's rows while the new id is in flight.
export const useInvestmentContributions = ({
  id,
  onChanged,
}: UseInvestmentContributionsProps): UseInvestmentContributionsReturn => {
  const dispatch = useDispatch();
  const [deleteTransaction] = useDeleteTransactionMutation();

  const base = useGetInvestmentTransfersQuery({
    id,
    page: 0,
    limit: PAGE_SIZE,
  });
  const [fetchMore, lazyResult] = useLazyGetInvestmentTransfersQuery();

  const [appended, setAppended] = useState<InvestmentTransferItem[]>([]);
  const [nextPage, setNextPage] = useState(1);
  const [latestPagination, setLatestPagination] = useState(
    base.currentData?.pagination,
  );

  useEffect(() => {
    setAppended([]);
    setNextPage(1);
    setLatestPagination(base.currentData?.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.fulfilledTimeStamp, id]);

  const notifyErrors = useCallback((err: any): string => {
    notify.apiError(err);
    return getErrorMessages(err)[0] ?? "Failed to load contributions";
  }, []);

  const items = useMemo(
    () => [...(base.currentData?.items ?? []), ...appended],
    [base.currentData?.items, appended],
  );

  const pagination = latestPagination ?? base.currentData?.pagination;

  const loadMore = useCallback(async () => {
    if (lazyResult.isFetching || !pagination?.hasNext) {
      return;
    }
    try {
      const result = await fetchMore({
        id,
        page: nextPage,
        limit: PAGE_SIZE,
      }).unwrap();
      setAppended((prev) => [...prev, ...result.items]);
      setLatestPagination(result.pagination);
      setNextPage((prev) => prev + 1);
    } catch (err) {
      notifyErrors(err);
    }
  }, [
    fetchMore,
    id,
    nextPage,
    pagination?.hasNext,
    lazyResult.isFetching,
    notifyErrors,
  ]);

  const reload = useCallback(async () => {
    await base.refetch();
  }, [base]);

  const removeContribution = useCallback(
    async (item: InvestmentTransferItem) => {
      try {
        // Only ever a legacy or manual row — a ledger transfer is deleted from
        // the Activity feed — so the narrow invalidation is the whole story.
        await deleteTransaction(item.id).unwrap();
        notify.success("Contribution deleted");
        dispatch(
          investmentsApi.util.invalidateTags([
            { type: "InvestmentTransfer" as const, id },
            { type: "Investment" as const, id },
          ]),
        );
        onChanged?.();
      } catch (err) {
        notifyErrors(err);
      }
    },
    [id, dispatch, onChanged, notifyErrors, deleteTransaction],
  );

  const error = base.error
    ? (getErrorMessages(base.error as any)[0] ?? "Failed to load contributions")
    : null;

  return {
    items,
    totalRows: pagination?.totalRows ?? 0,
    // Straight off the base page: the count spans the whole history and doesn't need the appended pages.
    hasNext: pagination?.hasNext ?? false,
    isLoading: base.isLoading || (base.isFetching && !base.currentData),
    isLoadingMore: lazyResult.isFetching,
    error,
    loadMore,
    removeContribution,
    reload,
  };
};
